/**
 * outboxPolicy.ts — how the sync outbox treats a failed upload.
 *
 * Every failure falls into one of three kinds:
 *   • blocked:   the device cannot reach the server or is not signed in. The
 *                item is not at fault, so it is left untouched and the whole
 *                pass is retried later.
 *   • temporary: the server could not take the item right now (rate limit,
 *                server error). Retried with a growing delay, up to a limit.
 *   • permanent: the server refused the item itself (page gone, bad data).
 *                Retrying cannot help, so it waits for the user.
 */

import type { StoredMutation } from '../platform/webStorage'

/** An HTTP error from the sync API, carrying the status code. */
export class SyncRequestError extends Error {
  constructor(message: string, public readonly status: number) {
    super(message)
    this.name = 'SyncRequestError'
  }
}

export type FailureKind = 'blocked' | 'temporary' | 'permanent'

export function classifyFailure(err: unknown): FailureKind {
  const status = err instanceof SyncRequestError ? err.status : undefined
  // fetch() rejecting (no status) means the request never reached the server.
  if (status === undefined) return 'blocked'
  if (status === 401 || status === 402) return 'blocked'
  if (status === 408 || status === 425 || status === 429 || status >= 500) return 'temporary'
  return 'permanent'
}

/**
 * Whether a failed batch may hide a single bad item, so splitting it would let
 * the rest through. Rate limits and timeouts are about the request, not its
 * contents.
 */
export function worthSplitting(err: unknown): boolean {
  if (!(err instanceof SyncRequestError)) return false
  const kind = classifyFailure(err)
  return kind === 'permanent' || (kind === 'temporary' && err.status >= 500)
}

/** Delay before the next automatic try, indexed by attempts made so far. */
const RETRY_DELAYS_MS = [5_000, 30_000, 120_000, 600_000]
/** Temporary failures in a row before an item waits for the user. */
const MAX_TEMPORARY_ATTEMPTS = 5
/**
 * A page's text or versions are refused with 404 until its folder/page row
 * reaches the server, and a new page's row is sent separately. So "not found"
 * is retried a few times (about 2.5 minutes) before it waits for the user.
 */
const NOT_FOUND_GRACE_ATTEMPTS = 3
/** Delays for retrying a whole pass while the server is unreachable. */
export const BLOCKED_RETRY_DELAYS_MS = [5_000, 15_000, 60_000, 300_000]

function retryDelay(attempts: number): number {
  return RETRY_DELAYS_MS[Math.min(Math.max(attempts, 1), RETRY_DELAYS_MS.length) - 1]
}

/** The delivery fields of an item after one more failed attempt. */
export function afterFailure(
  mutation: Pick<StoredMutation, 'attempts'>,
  err: unknown,
  now: number,
): Pick<StoredMutation, 'status' | 'attempts' | 'lastAttemptAt' | 'nextAttemptAt' | 'lastError' | 'httpStatus'> {
  const kind = classifyFailure(err)
  const attempts = (mutation.attempts ?? 0) + 1
  const notFound = err instanceof SyncRequestError && err.status === 404
  const giveUp = notFound
    ? attempts >= NOT_FOUND_GRACE_ATTEMPTS
    : kind === 'permanent' || attempts >= MAX_TEMPORARY_ATTEMPTS
  return {
    status: giveUp ? 'failed' : 'pending',
    attempts,
    lastAttemptAt: now,
    nextAttemptAt: giveUp ? undefined : now + retryDelay(attempts),
    lastError: err instanceof Error ? err.message : String(err),
    httpStatus: err instanceof SyncRequestError ? err.status : undefined,
  }
}

export function isDue(mutation: Pick<StoredMutation, 'status' | 'nextAttemptAt'>, now: number): boolean {
  return mutation.status === 'pending' && (mutation.nextAttemptAt ?? 0) <= now
}

/**
 * Page text items, queued by builds before page text moved entirely to each
 * page's edit log. Their text is already in the edit log, and the route they
 * targeted is gone, so they are dropped rather than sent.
 */
export function isRetiredPageText(mutation: Pick<StoredMutation, 'type' | 'action'>): boolean {
  return mutation.type === 'document' && mutation.action !== 'delete'
}

/**
 * Which queued items a new one replaces. Tree rows and settings carry the
 * whole current state of their target (last-writer-wins by HLC), so only the
 * newest copy needs sending. A page deletion makes any queued item or
 * snapshot for that page moot. Snapshots are distinct versions and are never
 * merged.
 */
export function supersedes(incoming: StoredMutation, queued: StoredMutation): boolean {
  if (queued.id === incoming.id) return false
  if (incoming.type === 'snapshot') return false

  if (incoming.type === 'document' && incoming.action === 'delete') {
    if (queued.type === 'document') return queued.targetId === incoming.targetId
    if (queued.type === 'snapshot') return payloadOf(queued)?.documentId === incoming.targetId
    return false
  }

  if (queued.type !== incoming.type || queued.targetId !== incoming.targetId) return false
  if (incoming.type === 'document') return false

  // Tree rows and settings: never let an older write replace a newer one,
  // which can happen when two failed pushes are queued out of order.
  const incomingHlc = Number(payloadOf(incoming)?.hlcTimestamp ?? incoming.hlc)
  const queuedHlc = Number(payloadOf(queued)?.hlcTimestamp ?? queued.hlc)
  return !(queuedHlc > incomingHlc)
}

/**
 * When an older queued write is newer than the incoming one, the incoming one
 * is dropped instead.
 */
export function isStale(incoming: StoredMutation, queued: StoredMutation): boolean {
  if (incoming.type !== 'project' && incoming.type !== 'settings') return false
  if (queued.type !== incoming.type || queued.targetId !== incoming.targetId) return false
  const incomingHlc = Number(payloadOf(incoming)?.hlcTimestamp ?? incoming.hlc)
  const queuedHlc = Number(payloadOf(queued)?.hlcTimestamp ?? queued.hlc)
  return queuedHlc > incomingHlc
}

function payloadOf(mutation: StoredMutation): any {
  try {
    return typeof mutation.payload === 'string' ? JSON.parse(mutation.payload) : mutation.payload
  } catch {
    return null
  }
}
